import React, { FC, useCallback } from "react";
import { generatePath, useNavigate } from "react-router";
import { useDevice } from "@lookiero/aurora";
import { useI18nMessage } from "@lookiero/i18n-react";
import { ProductPreview } from "../../../../../../domain/projections/ProductPreview";
import { useGetSimilarProducts } from "../../../../behaviors/useGetSimilarProducts";
import { ProductCarousel } from "../../../../components/productCarousel/ProductCarousel";
import { ProductGallery } from "../../../../components/productGallery/ProductGallery";
import { useStaticInfo } from "../../../../hooks/useStaticInfo";
import { DOMAIN, I18nMessages } from "../../../../i18n/i18n";
import { Routes } from "../../../../routing/routes";
import { List } from "../../../../tracking/tracking";
import { useTrackItemSelected } from "../../../../tracking/useTrackItemSelected";

interface SimilarProductsProps {
  readonly variantId: string;
  readonly onPressProduct?: (product: ProductPreview) => void;
}

const SimilarProducts: FC<SimilarProductsProps> = ({ variantId, onPressProduct }) => {
  const crossSellingSimilarTitleText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.CROSS_SELLING_SIMILAR_TITLE,
  });

  const { products: similarProducts } = useGetSimilarProducts(variantId);
  const { screen } = useDevice();
  const {
    session: { user },
    basePath,
    tradename,
  } = useStaticInfo();
  const navigate = useNavigate();

  const trackItemSelected = useTrackItemSelected({
    country: user.country,
    segment: user.segment,
    userId: user.id,
    tradename,
  });

  const handleOnPressProduct = useCallback(
    (product: ProductPreview) => {
      trackItemSelected({
        list: List.SIMILAR_PRODUCTS,
        productPreview: product,
      });

      onPressProduct?.(product);

      navigate(generatePath(`${basePath}/${Routes.PRODUCT_DETAIL}`, { variantId: product.id }), {
        state: { innerNavigation: true },
      });
    },
    [basePath, navigate, onPressProduct, trackItemSelected],
  );

  if (!similarProducts.length) {
    return null;
  }

  return screen.L ? (
    <ProductGallery
      products={similarProducts}
      title={crossSellingSimilarTitleText}
      onPressProduct={handleOnPressProduct}
    />
  ) : (
    <ProductCarousel
      products={similarProducts}
      title={crossSellingSimilarTitleText}
      onPressProduct={handleOnPressProduct}
    />
  );
};

export { SimilarProducts };
